import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from 'src/store';
import { ProductVideoSliceInitState } from './state';
import * as api from 'src/api/product';
import { BASE_URL } from 'src/config/api';
import { ProductVideo } from 'src/admin-entity/interface/product/product-video.interface';

const getUrl = (
    searchCategoryId: number,
    searchDistributor: string,
    searchProductName: string,
    searchVideoName: string,
    searchProductStatus: string,
): URL => {
    const url = new URL(`${BASE_URL}/v1/admin/video`);
    if (searchCategoryId !== 0) url.searchParams.append(`categoryId`, `${searchCategoryId}`);
    if (searchDistributor) url.searchParams.append(`distributorName`, searchDistributor);
    if (searchProductName) url.searchParams.append('productName', searchProductName);
    if (searchVideoName) url.searchParams.append('title', searchVideoName);
    if (searchProductStatus && searchProductStatus !== 'all-status')
        url.searchParams.append('productStatus', searchProductStatus);

    return url;
};

export const searchProductVideo = createAsyncThunk(
    `productVideoSlice/searchProductVideo`,
    async ({ page, take }: { page: number; take: number }, { getState, rejectWithValue }) => {
        try {
            const { searchCategory, searchDistributor, searchProductName, searchProductStatus, searchVideoName } = (
                getState() as AppState
            ).productVideo.searchCondition;
            const url = getUrl(
                searchCategory.id,
                searchDistributor,
                searchProductName,
                searchVideoName,
                searchProductStatus,
            );
            url.searchParams.append('page', `${page}`);
            url.searchParams.append('take', `${take}`);
            const res = await api.getVideoList(url.toString());
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const searchProductVideoForModal = createAsyncThunk(
    `productVideoSlice/searchProductVideoForModal`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const { searchCategory, searchDistributor, searchProductName, searchProductStatus, searchVideoName } = (
                getState() as AppState
            ).productVideo.searchConditionForModal;
            const { pageForModal, takeForModal } = (getState() as AppState).productVideo;
            const url = getUrl(
                searchCategory.id,
                searchDistributor,
                searchProductName,
                searchVideoName,
                searchProductStatus,
            );
            url.searchParams.append('page', `${pageForModal}`);
            url.searchParams.append('take', `${takeForModal}`);
            const res = await api.getVideoList(url.toString());
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const productVideoSlice = createSlice({
    name: 'productVideoSlice',
    initialState: ProductVideoSliceInitState,
    reducers: {
        initVideoSearch: state => {
            return {
                ...ProductVideoSliceInitState,
            };
        },
        initVideoSearchModal: state => {
            return {
                ...ProductVideoSliceInitState,
                searchConditionForModal: ProductVideoSliceInitState.searchCondition,
                pageForModal: ProductVideoSliceInitState.pageForModal,
                takeForModal: ProductVideoSliceInitState.takeForModal,
                searchResultForModal: ProductVideoSliceInitState.searchResultForModal,
            };
        },
        updateSearchCondition: (state, action: PayloadAction<{ key: string; value: string }>) => {
            return {
                ...state,
                searchCondition: {
                    ...state.searchCondition,
                    [action.payload.key]: action.payload.value,
                },
            };
        },
        updateSearchConditionModal: (state, action: PayloadAction<{ key: string; value: string }>) => {
            return {
                ...state,
                searchConditionForModal: {
                    ...state.searchConditionForModal,
                    [action.payload.key]: action.payload.value,
                },
            };
        },
        updateSearchCategory: (state, action: PayloadAction<number>) => {
            state.searchCondition.searchCategory = { ...state.searchCondition.searchCategory, id: action.payload };
        },
        updateSearchCategoryModal: (state, action: PayloadAction<number>) => {
            state.searchConditionForModal.searchCategory = {
                ...state.searchConditionForModal.searchCategory,
                id: action.payload,
            };
        },
        onChangeProductStatus: (state, action: PayloadAction<string>) => {
            state.searchCondition.searchProductStatus = action.payload;
        },
        onChangeProductStatusModal: (state, action: PayloadAction<string>) => {
            state.searchConditionForModal.searchProductStatus = action.payload;
        },
        setTake: (state, action: PayloadAction<number>) => {
            state.take = action.payload;
        },
        setTakeModal: (state, action: PayloadAction<number>) => {
            state.takeForModal = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setPageModal: (state, action: PayloadAction<number>) => {
            state.pageForModal = action.payload;
        },
        deleteVideo: (state, action: PayloadAction<number>) => {
            state.searchResult.list = state.searchResult.list.filter(p => p.id !== action.payload);
        },
        deleteVideoModal: (state, action: PayloadAction<number>) => {
            state.searchResultForModal.list = state.searchResultForModal.list.filter(p => p.id !== action.payload);
        },
        addProductVideos: (state, action: PayloadAction<ProductVideo[]>) => {
            state.selectedProductVideos.push(...action.payload);
        },
        addProductVideo: (state, action: PayloadAction<ProductVideo>) => {
            state.selectedProductVideos.push(action.payload);
        },
        removeProductVideo: (state, action: PayloadAction<ProductVideo>) => {
            state.selectedProductVideos = state.selectedProductVideos.filter(item => item.id !== action.payload.id);
        },
    },
    extraReducers: builder => {
        builder.addCase(searchProductVideo.fulfilled, (state, { payload }) => {
            state.searchResult.list = payload.list;
            state.searchResult.page = payload.page;
        });
        builder.addCase(searchProductVideoForModal.fulfilled, (state, { payload }) => {
            state.searchResultForModal.list = payload.list;
            state.searchResultForModal.page = payload.page;
        });
    },
});

export const {
    initVideoSearch,
    updateSearchCondition,
    updateSearchCategory,
    onChangeProductStatus,
    setTake,
    setPage,
    deleteVideo,
    updateSearchConditionModal,
    updateSearchCategoryModal,
    onChangeProductStatusModal,
    setTakeModal,
    setPageModal,
    deleteVideoModal,
    addProductVideos,
    addProductVideo,
    removeProductVideo,
    initVideoSearchModal,
} = productVideoSlice.actions;
export default productVideoSlice.reducer;
